import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosConstants from "./axiosConstants";

const settings = {
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Headers": "*",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": true,
    "X-XSRF-TOKEN": axiosConstants.XSRF,
    Accept: "application/json"
  }
};

interface AxiosInstanceType {
  [x: string]: any;
  get: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
  post: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  put: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  patch: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  delete: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
}

const getAxiosInstance = (
  _instance: AxiosInstance,
  getAdditionalHeaders?: () => Record<string, string>
) => {
  return {
    async get(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.get(url, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async post(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.post(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async put(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.put(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async patch(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.patch(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async delete(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.delete(url, {
          ...config,
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    }
  };
};

const baseURLAutomatedBilling =
  process.env.NODE_ENV === "production"
    ? "automated-billing-portlet"
    : "https://automated-billing-system.k8.isw.la/automated-billing/admin";

const instanceAutomatedBilling = axios.create({
  ...settings,
  baseURL: baseURLAutomatedBilling
});

if (process.env.NODE_ENV !== "production") {
  instanceAutomatedBilling.defaults.headers.common.Authorization =
    "Bearer eyJhbGciOiJSUzI1NiJ9.eyJsYXN0TmFtZSI6IklmZWFueWktT2tvcm9ua3dvIiwibWVyY2hhbnRfY29kZSI6Ik1YMTU3ODUiLCJmaXJzdExvZ2luIjpmYWxzZSwidXNlcl9uYW1lIjoiVG9jaGkuT2tvcm9ua3dvIiwicmVxdWVzdG9yX2lkIjoiMTIzOTg0ODQ3NzMiLCJkb21haW5fY29kZSI6IklTVyIsIm1vYmlsZU5vIjoiMjM0ODEzMzIxMzE5OCIsImNsaWVudF90eXBlIjoiREVGQVVMVCIsImFwcGxpY2F0aW9uX2lkIjoiNSIsInBheWFibGVfaWQiOiIzNjA0NCIsImNsaWVudF9pZCI6Imlzdy1wb3J0YWwiLCJpbnN0aXR1dGlvbl9pZCI6IjIwIiwiZmlyc3ROYW1lIjoiVG9jaGkiLCJlbWFpbFZlcmlmaWVkIjp0cnVlLCJhdWQiOlsiYXJiaXRlciIsImNhZXNhciIsImNhcmRsZXNzLXNlcnZpY2UiLCJoaW1zLXBvcnRsZXQiLCJpbmNvZ25pdG8iLCJpc3ctY29sbGVjdGlvbnMiLCJpc3ctY29yZSIsImlzdy1sZW5kaW5nLXNlcnZpY2UiLCJpc3ctcGF5bWVudGdhdGV3YXkiLCJpc3ctcG9ydGFsIiwiaXN3LXBvc3Qtb2ZmaWNlIiwibmlic3MtaWRyaXMtY29weS1zZXR0aW5ncyIsInBhc3Nwb3J0IiwicG9zdGNhcmQtYXBpIiwicHJvamVjdC14LW1lcmNoYW50IiwicmVjdXJyZW50LWJpbGxpbmctYXBpIiwicmVmZXJyYWwtc2VydmljZS1hcGkiLCJzYWZldG9rZW4tYWRtaW4tYXBpIiwidHJhbnNmZXItc2VydmljZS1hZG1pbiIsInRyYW5zZmVyLXNlcnZpY2UtY29yZSIsInRyYW5zZmVyLXNlcnZpY2UtcG9ydGxldCIsInRzLW5hbWUtZW5xdWlyeSIsInZhdWx0Iiwid2FsbGV0Iiwid2VicGF5LXBvcnRsZXQiXSwiY29yZV9pZCI6IjEwMCIsInNjb3BlIjpbInByb2ZpbGUiXSwiZXhwIjoxNzE1MTk0NjQyLCJtb2JpbGVOb1ZlcmlmaWVkIjp0cnVlLCJjbGllbnRfbmFtZSI6IklTVyBCdXNpbmVzcyBQb3J0YWwiLCJqdGkiOiI2NzRkMWRjNi01ODRhLTQzOTgtODU4Ni1mMjk2NTc0ZTkwMmEiLCJlbWFpbCI6IlRvY2hpLklmZWFueWktT2tvcm9ua3dvQGludGVyc3dpdGNoZ3JvdXAuY29tIiwicGFzc3BvcnRJZCI6IjI4QzA0OUQzLUVDQjMtMzhDQy1BNTE4LTg5RjJBRThFMENCNCJ9.bq91bF5CrUshRsg8nDsba8pOKH8PTb93nug9Iq3rRcvmYVgNB9XJSNnKpJjRmXWYZ4Y_cdltW5CiP2tHn5qzGR7GFGtFlYDk5MPb7u6Q50I2ZxhMo9Fc7A-kxk2au3Tkxra5oAs1kqv5IzpsCeHjpwP9VR1zIQXlLGxbaDj9pGLMoSVp77criE80APg8SMSg7TxMBhufuLs4TE4JxbHARBstAOleaCRhLhUoMCK_59KmBiw1uYg1IdBQOcoJmrifFSjWPab3mirjts7erL0aG8UcDFk8ZL8BG3uZVq2uj4F3yieE1zHLehEjIQ2deg_gz-zRSOz82hxg8g21-u5DAA";
}

export const axiosInstanceAutomatedBilling: AxiosInstanceType =
  getAxiosInstance(instanceAutomatedBilling);
